import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  SparklesIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  UserGroupIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { registrationService } from '../services/registrationService';
import { config } from '../config/environment';

type Role = 'coach' | 'client' | null;

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const accountDeleted = (location.state as any)?.accountDeleted === true;
  const redirectTo = searchParams.get('redirect') || null;

  const [role, setRole] = useState<Role>(searchParams.get('role') === 'client' ? 'client' : null);

  // Coach login form
  const [coachEmail, setCoachEmail] = useState('');
  const [coachPassword, setCoachPassword] = useState('');
  const [coachShowPw, setCoachShowPw] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState('');

  // Client login form
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientShowPw, setClientShowPw] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState('');

  // Forgot password — coach
  const [coachForgot, setCoachForgot] = useState(false);
  const [coachResetEmail, setCoachResetEmail] = useState('');
  const [coachResetMsg, setCoachResetMsg] = useState('');
  const [coachResetLoading, setCoachResetLoading] = useState(false);

  // Forgot password — client
  const [clientForgot, setClientForgot] = useState(false);
  const [clientResetEmail, setClientResetEmail] = useState('');
  const [clientResetMsg, setClientResetMsg] = useState('');
  const [clientResetLoading, setClientResetLoading] = useState(false);

  // Coach registration email-check modal
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regStatus, setRegStatus] = useState<'idle' | 'loading' | 'available' | 'existing_client' | 'existing_coach'>('idle');
  const [regClientData, setRegClientData] = useState<{ firstName?: string; lastName?: string; phone?: string }>({});

  // OTP state for new-email coach registration
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpChecking, setOtpChecking] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);

  // Admin login modal
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminShowPw, setAdminShowPw] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');

  const handleCoachLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setCoachLoading(true);
    setCoachError('');
    try {
      const response = await registrationService.loginCoach(coachEmail, coachPassword);
      if (response.success) {
        if (response.token) {
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('userType', 'coach');
          localStorage.setItem('userData', JSON.stringify({ ...response.user, must_verify: response.mustVerify }));
        }
        if (response.mustVerify) { navigate('/verify', { replace: true }); return; }
        navigate(redirectTo || '/dashboard/coach', { replace: true });
      } else {
        setCoachError(response.message || 'Login failed');
      }
    } catch {
      setCoachError('An unexpected error occurred');
    } finally {
      setCoachLoading(false);
    }
  };

  const handleClientLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientLoading(true);
    setClientError('');
    try {
      const response = await registrationService.loginClient(clientEmail, clientPassword);
      if (response.success) {
        if (response.token) {
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('userType', 'client');
          localStorage.setItem('userData', JSON.stringify({ ...response.user, must_verify: response.mustVerify, must_reset_password: response.mustResetPassword }));
        }
        if (response.mustVerify) { navigate('/verify', { replace: true }); return; }
        if (response.mustResetPassword) { navigate('/fill-profile', { replace: true }); return; }
        navigate(redirectTo || '/dashboard/client', { replace: true });
      } else {
        setClientError(response.message || 'Login failed');
      }
    } catch {
      setClientError('An unexpected error occurred');
    } finally {
      setClientLoading(false);
    }
  };

  const handleCoachForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setCoachResetLoading(true);
    setCoachResetMsg('');
    try {
      const result = await registrationService.requestPasswordReset(coachResetEmail);
      setCoachResetMsg(result.message || 'Reset link sent.');
    } catch {
      setCoachResetMsg('Something went wrong. Please try again.');
    } finally {
      setCoachResetLoading(false);
    }
  };

  const handleClientForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientResetLoading(true);
    setClientResetMsg('');
    try {
      const result = await registrationService.requestPasswordReset(clientResetEmail);
      setClientResetMsg(result.message || 'Reset link sent.');
    } catch {
      setClientResetMsg('Something went wrong. Please try again.');
    } finally {
      setClientResetLoading(false);
    }
  };

  const sendOtp = async (email: string) => {
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/verify/pre-register/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        setOtpError(data.detail || 'Failed to send verification code');
      }
    } catch {
      setOtpError('Failed to send verification code');
    } finally {
      setOtpSending(false);
    }
  };

  const checkOtp = async () => {
    setOtpChecking(true);
    setOtpError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/verify/pre-register/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regEmail, code: otpCode }),
      });
      if (res.ok) {
        setOtpVerified(true);
      } else {
        const data = await res.json();
        setOtpError(data.detail || 'Invalid or expired code');
      }
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setOtpChecking(false);
    }
  };

  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegStatus('loading');
    try {
      const res = await fetch(`${config.apiBaseUrl}/coaches/check-email?email=${encodeURIComponent(regEmail)}`);
      const data = await res.json();
      setRegStatus(data.status);
      if (data.status === 'existing_client') {
        setRegClientData({ firstName: data.first_name, lastName: data.last_name, phone: data.phone });
      }
      if (data.status === 'available') {
        setOtpCode(''); setOtpVerified(false); setOtpError('');
        await sendOtp(regEmail);
      }
    } catch {
      setRegStatus('available');
      setOtpCode(''); setOtpVerified(false); setOtpError('');
      await sendOtp(regEmail);
    }
  };

  const handleRegisterProceed = () => {
    const prefill = regStatus === 'existing_client' ? { ...regClientData, email: regEmail } : undefined;
    const emailToPass = regEmail;
    closeRegisterModal();
    navigate('/register/provider', { state: { email: emailToPass, prefill } });
  };

  const closeRegisterModal = () => {
    setShowRegisterModal(false);
    setRegEmail('');
    setRegStatus('idle');
    setRegClientData({});
    setOtpCode('');
    setOtpVerified(false);
    setOtpError('');
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await registrationService.loginAdmin(adminEmail, adminPassword);
      if (response.success && response.token && response.user) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userType', 'admin');
        localStorage.setItem('userData', JSON.stringify(response.user));
        navigate('/dashboard/admin', { replace: true });
      } else {
        setAdminError(response.message || 'Login failed');
      }
    } catch {
      setAdminError('An unexpected error occurred');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-violet-950 to-fuchsia-950 flex flex-col">
      {/* Decorative glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[300px] bg-fuchsia-600/15 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[200px] bg-violet-600/15 rounded-full blur-3xl" />
      </div>

      {accountDeleted && (
        <div className="relative z-10 bg-emerald-800 text-emerald-100 text-sm text-center py-3 px-6">
          Your account has been permanently deleted. We hope to see you again someday.
        </div>
      )}

      {/* Main */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {role === null ? (
              <motion.div
                key="role-picker"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
              >
                <div className="text-center mb-10">
                  <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
                  <p className="text-violet-300 text-sm">How would you like to sign in?</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setRole('coach')}
                    className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-fuchsia-500/50 rounded-2xl p-6 text-center transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-fuchsia-500/20 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-fuchsia-500/30 transition-colors">
                      <UserGroupIcon className="w-6 h-6 text-fuchsia-400" />
                    </div>
                    <p className="text-white font-semibold text-sm mb-1">Coach</p>
                    <p className="text-violet-400 text-xs">Manage your practice</p>
                  </button>

                  <button
                    onClick={() => setRole('client')}
                    className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-violet-500/50 rounded-2xl p-6 text-center transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-violet-500/30 transition-colors">
                      <UserIcon className="w-6 h-6 text-violet-400" />
                    </div>
                    <p className="text-white font-semibold text-sm mb-1">Client</p>
                    <p className="text-violet-400 text-xs">Track your progress</p>
                  </button>
                </div>

                <p className="text-center text-xs text-violet-500">
                  New coach?{' '}
                  <button
                    onClick={() => { setShowRegisterModal(true); setRegStatus('idle'); setRegEmail(''); }}
                    className="text-fuchsia-400 hover:text-fuchsia-300 font-medium transition-colors"
                  >
                    Register here
                  </button>
                </p>
              </motion.div>
            ) : role === 'coach' ? (
              <motion.div
                key="coach-form"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
              >
                <button
                  onClick={() => { setRole(null); setCoachForgot(false); setCoachError(''); setCoachResetMsg(''); }}
                  className="flex items-center gap-1.5 text-violet-400 hover:text-violet-300 text-sm mb-6 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Back
                </button>

                <div className="mb-8">
                  <div className="w-10 h-10 bg-fuchsia-500/20 rounded-xl flex items-center justify-center mb-3">
                    <UserGroupIcon className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    {coachForgot ? 'Reset your password' : 'Coach login'}
                  </h2>
                </div>

                {coachForgot ? (
                  <form onSubmit={handleCoachForgot} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Email address</label>
                      <input
                        type="email"
                        value={coachResetEmail}
                        onChange={e => setCoachResetEmail(e.target.value)}
                        className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                        placeholder="your@email.com"
                        required
                        autoFocus
                      />
                    </div>
                    {coachResetMsg && (
                      <div className="p-3 bg-blue-900/30 border border-blue-800 rounded-xl">
                        <p className="text-blue-300 text-sm">{coachResetMsg}</p>
                      </div>
                    )}
                    <button type="submit" disabled={coachResetLoading} className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${coachResetLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}>
                      {coachResetLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending...</> : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => { setCoachForgot(false); setCoachResetMsg(''); }} className="w-full text-sm text-violet-400 hover:text-violet-300 transition-colors">
                      Back to login
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleCoachLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                      <input
                        type="email"
                        value={coachEmail}
                        onChange={e => setCoachEmail(e.target.value)}
                        className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                        placeholder="your@email.com"
                        required
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Password</label>
                      <div className="relative">
                        <input
                          type={coachShowPw ? 'text' : 'password'}
                          value={coachPassword}
                          onChange={e => setCoachPassword(e.target.value)}
                          className="w-full px-3 py-2.5 pr-10 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                          placeholder="Enter your password"
                          required
                        />
                        <button type="button" onClick={() => setCoachShowPw(!coachShowPw)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          {coachShowPw ? <EyeSlashIcon className="h-4 w-4 text-violet-500" /> : <EyeIcon className="h-4 w-4 text-violet-500" />}
                        </button>
                      </div>
                    </div>
                    {coachError && (
                      <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                        <p className="text-red-300 text-sm">{coachError}</p>
                      </div>
                    )}
                    <button type="submit" disabled={coachLoading} className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${coachLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}>
                      {coachLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Signing in...</> : <><KeyIcon className="w-4 h-4 mr-2" />Sign in</>}
                    </button>
                    <div className="flex items-center justify-between text-xs">
                      <button type="button" onClick={() => setCoachForgot(true)} className="text-violet-400 hover:text-violet-300 transition-colors">
                        Forgot password?
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowRegisterModal(true); setRegStatus('idle'); setRegEmail(''); }}
                        className="text-fuchsia-400 hover:text-fuchsia-300 font-medium transition-colors"
                      >
                        Register as coach
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="client-form"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
              >
                <button
                  onClick={() => { setRole(null); setClientForgot(false); setClientError(''); setClientResetMsg(''); }}
                  className="flex items-center gap-1.5 text-violet-400 hover:text-violet-300 text-sm mb-6 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Back
                </button>

                <div className="mb-8">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center mb-3">
                    <UserIcon className="w-5 h-5 text-violet-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    {clientForgot ? 'Reset your password' : 'Client portal'}
                  </h2>
                </div>

                {clientForgot ? (
                  <form onSubmit={handleClientForgot} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Email address</label>
                      <input
                        type="email"
                        value={clientResetEmail}
                        onChange={e => setClientResetEmail(e.target.value)}
                        className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="your@email.com"
                        required
                        autoFocus
                      />
                    </div>
                    {clientResetMsg && (
                      <div className="p-3 bg-blue-900/30 border border-blue-800 rounded-xl">
                        <p className="text-blue-300 text-sm">{clientResetMsg}</p>
                      </div>
                    )}
                    <button type="submit" disabled={clientResetLoading} className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${clientResetLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}>
                      {clientResetLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending...</> : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => { setClientForgot(false); setClientResetMsg(''); }} className="w-full text-sm text-violet-400 hover:text-violet-300 transition-colors">
                      Back to login
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleClientLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={e => setClientEmail(e.target.value)}
                        className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="your@email.com"
                        required
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Password</label>
                      <div className="relative">
                        <input
                          type={clientShowPw ? 'text' : 'password'}
                          value={clientPassword}
                          onChange={e => setClientPassword(e.target.value)}
                          className="w-full px-3 py-2.5 pr-10 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                          placeholder="Enter your password"
                          required
                        />
                        <button type="button" onClick={() => setClientShowPw(!clientShowPw)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          {clientShowPw ? <EyeSlashIcon className="h-4 w-4 text-violet-500" /> : <EyeIcon className="h-4 w-4 text-violet-500" />}
                        </button>
                      </div>
                    </div>
                    {clientError && (
                      <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                        <p className="text-red-300 text-sm">{clientError}</p>
                      </div>
                    )}
                    <button type="submit" disabled={clientLoading} className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${clientLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}>
                      {clientLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Signing in...</> : <><KeyIcon className="w-4 h-4 mr-2" />Sign in</>}
                    </button>
                    <div className="flex items-center justify-between text-xs">
                      <button type="button" onClick={() => setClientForgot(true)} className="text-violet-400 hover:text-violet-300 transition-colors">
                        Forgot password?
                      </button>
                      <span className="text-violet-600 italic">Your coach creates your account</span>
                    </div>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-4 text-center text-violet-600 text-xs space-x-4 flex-shrink-0">
        <Link to="/" className="hover:text-violet-400 transition-colors">&larr; Back to site</Link>
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-violet-400 transition-colors">Terms</a>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-violet-400 transition-colors">Privacy</a>
        <a href="/support" className="hover:text-violet-400 transition-colors">Support</a>
      </footer>

      {/* Coach registration modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={closeRegisterModal}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-indigo-950 border border-white/10 rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-fuchsia-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <SparklesIcon className="w-6 h-6 text-fuchsia-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Register as Coach</h2>
              <p className="text-violet-400 text-xs mt-1">Enter your email to get started</p>
            </div>

            {regStatus === 'idle' || regStatus === 'loading' ? (
              <form onSubmit={handleEmailCheck} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                  <input
                    type="email"
                    value={regEmail}
                    onChange={e => setRegEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                    placeholder="your@email.com"
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={regStatus === 'loading'}
                  className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${regStatus === 'loading' ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                >
                  {regStatus === 'loading' ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Checking...</> : 'Continue'}
                </button>
                <button type="button" onClick={closeRegisterModal} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">
                  Cancel
                </button>
              </form>
            ) : regStatus === 'available' ? (
              <div className="space-y-4">
                {otpVerified ? (
                  <>
                    <div className="flex items-center gap-2 p-3 bg-green-900/30 border border-green-700 rounded-xl">
                      <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <p className="text-green-300 text-sm">Email verified! Let's set up your coach profile.</p>
                    </div>
                    <button onClick={handleRegisterProceed} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                      Continue to Registration
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-violet-300 text-sm text-center">
                      {otpSending ? 'Sending verification code...' : `We sent a 6-digit code to ${regEmail}`}
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">Verification Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-3 py-2 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500 text-center tracking-widest text-lg"
                        placeholder="000000"
                        autoFocus
                      />
                    </div>
                    {otpError && <p className="text-red-400 text-sm">{otpError}</p>}
                    <button
                      type="button"
                      onClick={checkOtp}
                      disabled={otpCode.length !== 6 || otpChecking}
                      className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${otpCode.length !== 6 || otpChecking ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                    >
                      {otpChecking ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Verifying...</> : 'Verify Code'}
                    </button>
                    <button type="button" onClick={() => sendOtp(regEmail)} disabled={otpSending} className="w-full text-sm text-fuchsia-400 hover:text-fuchsia-300 transition-colors disabled:opacity-50">
                      {otpSending ? 'Sending...' : 'Resend code'}
                    </button>
                  </>
                )}
                <button type="button" onClick={closeRegisterModal} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">
                  Cancel
                </button>
              </div>
            ) : regStatus === 'existing_client' ? (
              <div className="space-y-4">
                <div className="p-3 bg-violet-900/30 border border-violet-700 rounded-xl">
                  <p className="text-violet-200 text-sm">You already have a client account with this email. You can add a coach profile to your existing account.</p>
                </div>
                <button onClick={handleRegisterProceed} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                  Yes, Add Coach Profile
                </button>
                <button type="button" onClick={closeRegisterModal} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                  <p className="text-red-300 text-sm">A coach account already exists for this email. Please sign in instead.</p>
                </div>
                <button type="button" onClick={closeRegisterModal} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                  Back to Login
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Admin login modal */}
      {showAdminLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowAdminLogin(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-indigo-950 border border-white/10 rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-violet-900/40 rounded-full flex items-center justify-center mx-auto mb-3">
                <KeyIcon className="w-6 h-6 text-violet-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Admin Login</h2>
              <p className="text-violet-400 text-xs mt-1">CoachSight internal administration</p>
            </div>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} className="w-full px-3 py-2 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="admin@pracmagic.com" required autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Password</label>
                <div className="relative">
                  <input type={adminShowPw ? 'text' : 'password'} value={adminPassword} onChange={e => setAdminPassword(e.target.value)} className="w-full px-3 py-2 pr-10 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Enter your password" required />
                  <button type="button" onClick={() => setAdminShowPw(!adminShowPw)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {adminShowPw ? <EyeSlashIcon className="h-4 w-4 text-violet-500" /> : <EyeIcon className="h-4 w-4 text-violet-500" />}
                  </button>
                </div>
              </div>
              {adminError && <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl"><p className="text-red-300 text-sm">{adminError}</p></div>}
              <button type="submit" disabled={adminLoading} className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${adminLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-violet-700 hover:bg-violet-600 text-white'}`}>
                {adminLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Signing in...</> : <><KeyIcon className="w-4 h-4 mr-2" />Login</>}
              </button>
              <button type="button" onClick={() => setShowAdminLogin(false)} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">Cancel</button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
